import {
  Box,
  Button,
  Card,
  EnhancedDimension,
  Icon,
  Text,
  colors as gammaColors,
} from '@factorialco/deprecated-design-system'
import { ResponsiveBar } from '@nivo/bar'
import { AtsHiringStagesInsightsTimePerStageBarChartFragment } from 'generated/resources/graphql'
import { max, range } from 'lodash'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { getFormatMinutesLabel } from './useFormatMinutes'

const HEIGHTS_PER_ITEM: Record<number, EnhancedDimension> = {
  1: 's200',
  2: 's240',
  3: 's300',
  4: 's400',
  5: 's400',
}

const DAY_MINUTES = 1_440
const WEEK_MINUTES = 7 * DAY_MINUTES
const MONTH_MINUTES = 30 * DAY_MINUTES
const MAX_DAYS_TO_SHOW = 14

interface Props {
  stagesData: AtsHiringStagesInsightsTimePerStageBarChartFragment[]
  exportUrlParams: string | string[][]
  onExport: () => void
}

const tickToDays = (i: number) => i * DAY_MINUTES
const tickToWeeks = (i: number) => ((i / 4) | 0) * MONTH_MINUTES + (i % 4) * WEEK_MINUTES

export const TimePerStageBarChart: React.FC<Props> = ({
  stagesData,
  exportUrlParams,
  onExport,
}) => {
  const keys = ['value']
  const colors = [gammaColors.viridian1000]
  const data = stagesData
    .flatMap(({ hiringStage, averageMinutes }) =>
      hiringStage && hiringStage.name != 'hired'
        ? [
            {
              id: hiringStage.label,
              value: averageMinutes,
            },
          ]
        : []
    )
    // adds index to stage names to avoid mixing stages with the same name
    .map((stage, i) => ({ ...stage, id: `${i + 1}. ${stage.id}` }))
    .reverse()
  const totalDays = Math.ceil((max(data.map((d) => d.value)) ?? 1) / DAY_MINUTES)
  const totalWeeks = Math.ceil(totalDays / 7)
  const totalTicks = totalDays < MAX_DAYS_TO_SHOW ? totalDays : totalWeeks
  const tickToInterval = totalDays < MAX_DAYS_TO_SHOW ? tickToDays : tickToWeeks
  const ticks = range(1, totalTicks).map((i) => tickToInterval(i))

  const exportUrl = () =>
    buildUrl(`/ats/insights/hiring_stages?${new URLSearchParams(exportUrlParams).toString()}`)

  return (
    <Card>
      <Card.Title
        tooltip={i18n.t('ats.insights_page.hiring_stages_bar_chart.titleTooltip')}
        action={
          <Button.Router
            onClick={onExport}
            to={exportUrl()}
            type="secondary"
            iconName="download"
            download
          />
        }
      >
        {i18n.t('ats.insights_page.hiring_stages_bar_chart.title')}
      </Card.Title>
      <Card.Content>
        {data.length > 0 ? (
          <Box height={HEIGHTS_PER_ITEM[data.length] ?? 's600'} width="full" paddingY="s32">
            <ResponsiveBar
              data={data}
              keys={keys}
              margin={{
                right: 50,
                left: 120,
                bottom: 20,
              }}
              colors={colors}
              borderRadius={4}
              padding={0.5}
              layout="horizontal"
              enableLabel={false}
              theme={{
                fontSize: 12,
                fontFamily: 'Inter',
                textColor: gammaColors.grey800,
              }}
              layers={['grid', 'axes', 'bars']}
              axisLeft={{
                tickSize: 0,
                tickPadding: 20,
              }}
              enableGridY={false}
              enableGridX
              gridXValues={ticks}
              axisBottom={{
                tickValues: ticks,
                renderTick: (tick) => (
                  <g transform={`translate(${tick.x},${tick.y + 12})`}>
                    <text
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontFamily: 'Inter',
                        fill: gammaColors.grey800,
                        fontSize: 12,
                      }}
                    >
                      {getFormatMinutesLabel(tick.value)}
                    </text>
                  </g>
                ),
              }}
              tooltip={(tooltipData) => (
                <Box
                  background="grey800"
                  padding="s12"
                  borderRadius={{ all: 'abs008' }}
                  maxHeight="s240"
                >
                  <Text.Normal color="grey000White" size="200" weight="medium">
                    {getFormatMinutesLabel(tooltipData.data.value)}
                  </Text.Normal>
                </Box>
              )}
            />
          </Box>
        ) : (
          <Box paddingY="s24" gap="s8" width="full">
            <Box maxWidth="s600" textAlign="center" alignItems="center" alignSelf="center">
              <Icon.Large icon="document-folder-drawer-2" color="grey600" />
              <Text.Header2 color="grey1000">
                {i18n.t('ats.insights_page.hiring_stages_bar_chart.no_data.title')}
              </Text.Header2>
              <Text.Normal size="300" color="grey800">
                {i18n.t('ats.insights_page.hiring_stages_bar_chart.no_data.description')}
              </Text.Normal>
            </Box>
          </Box>
        )}
      </Card.Content>
    </Card>
  )
}
